import type { RevisionSummary, Track } from './types';

const identifiers: Record<number, string> = {
  1: 'Unsorted input plus repeated lookup, counting, or complement checks.',
  2: 'Sorted input, a pair relationship, or boundaries that can move inward safely.',
  3: 'A contiguous range where adding right and removing left updates validity.',
  4: 'Many range queries, subarray totals, or “everything before this index.”',
  5: 'Character frequency, normalization, substring, or repeated pattern matching.',
  6: 'Nested structure, undo order, or the next greater/smaller unresolved item.',
  7: 'First-in-first-out work, level processing, or a moving window extreme.',
  8: 'Node connections must change in place without losing the remaining chain.',
  9: 'A sorted or monotonic search space with a single false/true boundary.',
  10: 'The answer is naturally defined using a smaller version of the same problem.',
  11: 'Ordering exposes the relationship; adjacent or grouped items become easier to compare.',
  12: 'Hierarchical input where every subtree can return the same kind of answer.',
  13: 'Ordered left/right subtrees or a need to validate values against ancestor bounds.',
  14: 'Repeated access to the smallest/largest item or a streaming top-k requirement.',
  15: 'A custom state needs fast membership, frequency, grouping, or deduplication.',
  16: 'Two-dimensional neighbors, connected cells, or repeated boundary-safe movement.',
  17: 'Ranges overlap, cover time, or must be processed in start-order.',
  18: 'Mixed constraints: identify the invariant before choosing a familiar pattern.',
  19: 'Generate all valid choices while undoing state and pruning impossible branches.',
  20: 'Relationships form nodes and edges; the question asks reachability or components.',
  21: 'Directed prerequisites must be ordered, or a dependency cycle must be detected.',
  22: 'Components merge over time and connectivity queries repeat.',
  23: 'Find the cheapest path in a graph with nonnegative weighted edges.',
  24: 'Two nodes share ancestry and their root paths meet at a lowest split point.',
  25: 'Many strings share prefixes or require prefix lookup character by character.',
  26: 'Overlapping one-dimensional subproblems ask for best/count/possible up to index i.',
  27: 'Two independent state dimensions change, commonly grid position or two prefixes.',
  28: 'Choose items under a target/capacity with once-only or unlimited reuse.',
  29: 'Find an ordered subsequence while preserving the smallest possible tail per length.',
  30: 'A locally optimal decision appears safe and can be justified by exchange or reach.',
  31: 'Parity, subsets, flags, powers of two, or paired values suggest binary operations.',
  32: 'Divisibility, primes, GCD, modular arithmetic, or repeated factor structure.',
  33: 'Range queries and point/range updates are both frequent, making prefix sums insufficient.',
  34: 'A variable window needs multiple counts or exactly-k/at-most-k reasoning.',
  35: 'The graph also carries state, multiple sources, transformations, or layered constraints.',
  36: 'The hard part is defining state and transition, not recognizing one named problem.',
  37: 'A timed mixed problem: classify constraints, state the invariant, then implement.',
  38: 'A novel story hides a known invariant; map it before thinking about syntax.',
  39: 'Traffic, latency, availability, and cost must be balanced beyond one machine.',
  40: 'Requirements are vague and the architecture depends on traffic/storage estimates.',
  41: 'Many service instances need fair routing, health checks, and failure removal.',
  42: 'Reads dominate and repeated data can trade controlled freshness for lower latency.',
  43: 'Users are globally distributed and large cacheable assets dominate delivery.',
  44: 'Different access patterns and invariants require a deliberate storage model.',
  45: 'Reads need acceleration and scale, but writes and replica lag still matter.',
  46: 'One database no longer fits; a stable shard key must distribute data and traffic.',
  47: 'A network partition forces an explicit consistency-versus-availability behavior.',
  48: 'Producers and consumers run at different speeds or work must happen asynchronously.',
  49: 'Capacity or fairness needs enforcement across many distributed gateway instances.',
  50: 'Cluster membership changes and keys should move as little as possible.',
  51: 'A tiny write path creates keys; a massive latency-sensitive read path redirects.',
  52: 'Persistent connections, message ordering, offline delivery, and multi-device sync.',
  53: 'Content must be fanned out and ranked despite extremely skewed follower counts.',
  54: 'Large media moves through asynchronous processing and adaptive global delivery.',
  55: 'High-frequency locations feed a low-latency, concurrency-safe matching decision.',
  56: 'Bursty events must respect preferences and provider limits without duplicate delivery.',
  57: 'A behavioral prompt asks for evidence of judgment, collaboration, and measurable impact.',
  58: 'A resume claim must survive ownership, architecture, tradeoff, and failure follow-ups.',
  59: 'Two sorted collections plus a valid left/right partition suggest binary search on partition.',
  60: 'Final preparation should consolidate evidence and routine, not add random new breadth.',
};

const complexities: Record<number, string> = {
  1: 'Usually O(n) time · O(n) space', 2: 'O(n) time · O(1) space', 3: 'Usually O(n) time',
  4: 'O(n) build · O(1) range query', 5: 'Usually O(n) time', 6: 'O(n) time · O(n) stack',
  7: 'O(n) with a monotonic deque', 8: 'O(n) time · O(1) extra space', 9: 'O(log n) time',
  10: 'Often exponential; prune early', 11: 'Usually O(n log n)', 12: 'O(n) nodes visited',
  13: 'O(n) validation · O(h) stack', 14: 'O(n log k) for top-k', 15: 'Average O(1) lookup',
  16: 'O(rows × columns)', 17: 'O(n log n) for sorting', 18: 'Complexity follows the chosen invariant',
  19: 'Exponential output space', 20: 'O(V + E)', 21: 'O(V + E)', 22: 'Near O(1) amortized',
  23: 'O((V + E) log V)', 24: 'O(n) time · O(h) stack', 25: 'O(word length)', 26: 'O(n)',
  27: 'Usually O(rows × columns)', 28: 'O(items × capacity)', 29: 'O(n log n)', 30: 'Usually O(n) after sorting/state',
  31: 'O(n) time · O(1) space', 32: 'Sieve: O(n log log n)', 33: 'O(log n) update/query', 34: 'Usually O(n)',
  35: 'State count × outgoing transitions', 36: 'States × transition cost', 37: 'Budget before coding: 5–10 minutes',
  38: 'Derive before optimizing', 59: 'O(log min(m, n))',
};

export function getRevision(day: number, track: Track, concepts: [string, string, string]): RevisionSummary {
  return {
    label: track === 'DSA' || day === 59 ? 'Pattern identifier' : track === 'System Design' ? 'Main points to remember' : 'Quick recall',
    identifier: identifiers[day],
    bullets: concepts,
    complexity: complexities[day],
  };
}
